import React, { useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Upload, Image as ImageIcon, X, Trash2 } from 'lucide-react';

export default function UploadSection({
  uploadMode,
  onUploadModeChange,
  onImageUpload,
  uploadedImages,
  onRemoveImage,
  onClearAll
}) {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      onImageUpload(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onImageUpload(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className="space-y-4">
      {/* Upload Mode Toggle */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Upload Mode</label>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant={uploadMode === 'single' ? 'default' : 'outline'}
            onClick={() => onUploadModeChange('single')}
            size="sm"
            className="rounded-xl"
          >
            Single Photo
          </Button>
          <Button
            variant={uploadMode === 'multiple' ? 'default' : 'outline'}
            onClick={() => onUploadModeChange('multiple')}
            size="sm"
            className="rounded-xl"
          >
            Multiple Photos
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {uploadMode === 'single'
            ? 'One design repeated across all badges'
            : 'Different designs for each badge position'}
        </p>
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-border hover:border-primary rounded-2xl p-8 text-center cursor-pointer transition-all duration-300 hover:bg-primary/5 group"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <Upload className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="font-medium mb-1">Click to upload or drag & drop</p>
            <p className="text-sm text-muted-foreground">PNG, JPG, JPEG (max 10MB)</p>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={uploadMode === 'multiple'}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Uploaded Images */}
      {uploadedImages.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">
                {uploadedImages.length} Image{uploadedImages.length > 1 ? 's' : ''}
              </span>
            </div>
            {uploadedImages.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                className="text-destructive hover:text-destructive h-8 rounded-lg"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            )}
          </div>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {uploadedImages.map((image, index) => (
              <div
                key={image.id}
                className="flex items-center gap-3 p-2 rounded-xl bg-muted/50 border border-border group hover:border-primary/50 transition-all duration-200"
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <img
                    src={image.url}
                    alt={image.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{image.name}</p>
                  <Badge variant="outline" className="h-5 text-xs mt-1">
                    Image {index + 1}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveImage(image.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-lg hover:bg-destructive/10 hover:text-destructive"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
